/**
 * The below-`lg` layout of `../TextImageSlide.tsx`: slides stacked on the primary background,
 * separated by a divider, each with the photo first, then the heading, paragraphs, the two bullet
 * lists one after the other, and the call-to-action link. It draws no section title, because the
 * `AllSections` accordion tab that holds it shows the title. The desktop counterpart is
 * `../Desktop/TextImageSlideDesktop.tsx`.
 */
import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { ButtonLink } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection } from "@ryerson/frontend.layout";
import { TextImageSlideProps, Section, Bullet } from "../TextImageSlide";

const Divider = styled.hr`
	width: 100%;
	margin-bottom: 18px;
	margin-top: 0px;
	${(props: any) => {
		const { theme } = props;
		return css`
			color: ${theme.colors.primary.secondaryBrand};
		`;
	}}
`;

/** Mobile equipment and process slides; see the file header. */
const TextImageSlideMobile: React.FC<TextImageSlideProps> = (props) => {
	const { theme } = useTheme();
	return (
		<>
			{props.sections.map((section: Section, index: number) => (
				<React.Fragment key={section.title}>
					<ContentSection type="primary" hPadding="0">
						{index !== 0 ? <Divider theme={theme} /> : <></>}
						<img
							css={{ width: "100%" }}
							src={section.imageUrl}
							alt={section.title}
						></img>
						<Typography variant="h4">{section.title}</Typography>
						<Typography variant="p" size="md">
							{section.description}
						</Typography>
						{/* When `description2` is set, this prints `description` again;
						    `description2` itself isn't shown below `lg`. */}
						{section.description2 ? (
							<>
								<Typography variant="p" size="md">
									{section.description}
								</Typography>
							</>
						) : (
							<></>
						)}
						{section.descriptionArray &&
							section.descriptionArray.map((description: string, idx: number) => (
								<React.Fragment key={idx}>
									<Typography
										variant="p"
										size="md"
										css={css`
											white-space: pre-wrap;
										`}
									>
										{description}
									</Typography>
								</React.Fragment>
							))}
						<>
							{section.benefitsSection ? (
								<>
									<Typography variant="p" size="md" weight="bold">
										{section.benefitsSection.title}
									</Typography>

									<ul
										style={{
											paddingLeft: "17.5px",
										}}
									>
										{section.benefitsSection.bullet.map(
											(bullet: Bullet, idx: number) => (
												<li key={idx}>
													{bullet.bold ? (
														<>
															<Typography
																variant="span"
																weight="bold"
																size="xs"
															>
																{bullet.bold}
															</Typography>
															<Typography variant="span" size="xs">
																{bullet.normal}
															</Typography>
														</>
													) : (
														<Typography variant="span" size="xs">
															{bullet.normal}
														</Typography>
													)}
												</li>
											)
										)}
									</ul>
								</>
							) : (
								<></>
							)}
						</>
						<>
							{section.capabilitiesSection ? (
								<>
									<Typography variant="p" size="md" weight="bold">
										{section.capabilitiesSection.title}
									</Typography>

									<ul
										style={{
											paddingLeft: "17.5px",
										}}
									>
										{section.capabilitiesSection.bullet.map(
											(bullet: Bullet, idx: number) => (
												<li key={idx}>
													{bullet.bold ? (
														<>
															<Typography
																variant="span"
																weight="bold"
																size="xs"
															>
																{bullet.bold}
															</Typography>
															<Typography variant="span" size="xs">
																{bullet.normal}
															</Typography>
														</>
													) : (
														<Typography variant="span" size="xs">
															{bullet.normal}
														</Typography>
													)}
												</li>
											)
										)}
									</ul>
								</>
							) : (
								<></>
							)}
						</>
						{/* As on desktop, the `#` part of `actionLink` is passed as `hash`. */}
						<ButtonLink
							next={true}
							to={section.actionLink?.split("#")[0] ?? "#"}
							{...(section.actionLink
								? { hash: section.actionLink.split("#")[1] }
								: {})}
							label={section.actionText}
							orientation="left"
							linkType="internal"
						/>
					</ContentSection>
				</React.Fragment>
			))}
		</>
	);
};

export default TextImageSlideMobile;
